// src/components/PurchaseFlow/Steps/DataEntry/Sections/BankInformation.js
import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import { faCheckCircle, faExclamationTriangle, faBuildingColumns, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BankSelectionModal from '../../../../Modals/BankSelectionModal';
import { Button } from '../../../../ui';
import { useBanksCatalog } from '../../../../../hooks/useBanksCatalog';
import { isBankInCatalog } from '../../../../../services/banksCatalog';

const BankInformation = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    sectionValidity,
    setSectionValidity,
    handleSectionClick,
    bankData,
    onBankChange,
    clientData,
    displayMode = 'accordion',
    cbuLabel = 'CBU',
    requireBankSelection = false,
  } = props;

  const isContinuousMode = displayMode === 'continuous';

  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [showBankModal, setShowBankModal] = useState(false);
  const [invalidBankMessage, setInvalidBankMessage] = useState(false);
  const clearedInvalidBankIdRef = useRef(null);

  const {
    banks,
    loading: banksLoading,
    error: banksError,
    retry: retryBanks,
  } = useBanksCatalog({ enabled: requireBankSelection });

  const requiredFields = ['cbu'];

  const selectedBankValue = bankData?.id && isBankInCatalog(bankData.id, banks)
    ? String(bankData.id)
    : '';

  useEffect(() => {
    const allFieldsTouched = requiredFields.every((field) => touchedFields[field]);
    if (allFieldsTouched) {
      validateSection();
    }
    // eslint-disable-next-line
  }, [touchedFields]);

  useEffect(() => {
    if (bankData && formData.cbu) {
      validateSection();
    }
    // eslint-disable-next-line
  }, [bankData, formData.cbu]);

  useEffect(() => {
    if (!requireBankSelection || banksLoading || banksError) {
      return;
    }

    if (bankData?.id && banks.length > 0 && !isBankInCatalog(bankData.id, banks)) {
      setInvalidBankMessage(true);
      if (clearedInvalidBankIdRef.current === bankData.id) {
        return;
      }
      clearedInvalidBankIdRef.current = bankData.id;
      if (onBankChange) {
        onBankChange(null);
      }
    }
    // eslint-disable-next-line
  }, [requireBankSelection, banks, banksLoading, banksError, bankData?.id]);

  useEffect(() => {
    if (requireBankSelection) {
      validateSection();
    }
    // eslint-disable-next-line
  }, [requireBankSelection, banks, banksLoading, banksError, bankData, formData.cbu]);

  const validateCBU = (cbu) => {
    if (!cbu) {
      return 'El CBU es requerido';
    }

    const cleanCBU = cbu.replace(/[\s-]/g, '');

    if (!/^\d{22}$/.test(cleanCBU)) {
      return 'El CBU debe tener exactamente 22 dígitos';
    }

    return '';
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'cbu':
        error = validateCBU(value);
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    return !error;
  };

  const validateSection = () => {
    if (requireBankSelection) {
      if (banksLoading || banksError || banks.length === 0) {
        setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
        return false;
      }

      if (!bankData?.id || !isBankInCatalog(bankData.id, banks)) {
        setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
        return false;
      }
    } else if (!bankData || !bankData.id) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    const cbuValid = validateField('cbu', formData.cbu);

    if (!cbuValid) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
    return true;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cbu') {
      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length <= 22) {
        handleChange({ target: { name, value: cleanValue } });
      }
    } else {
      handleChange(e);
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleBankChangeClick = () => {
    setShowBankModal(true);
  };

  const handleBankConfirm = (clientWithBank) => {
    if (onBankChange) {
      onBankChange(clientWithBank.bank);
    }
    setShowBankModal(false);
  };

  const handleBankModalClose = () => {
    setShowBankModal(false);
  };

  const handleBankSelectChange = (e) => {
    const bankId = e.target.value;
    setInvalidBankMessage(false);
    clearedInvalidBankIdRef.current = null;

    if (!bankId) {
      if (onBankChange) {
        onBankChange(null);
      }
      return;
    }

    const bank = banks.find((item) => String(item.id) === String(bankId));
    if (bank && onBankChange) {
      onBankChange({
        id: bank.id,
        name: bank.name,
        entity_number: bank.entity_number,
      });
    }
  };

  const allowEditBankInfo = process.env.REACT_APP_ALLOW_EDIT_BANK_INFO === 'true';

  useImperativeHandle(ref, () => ({
    validateSection,
  }));

  const renderBankField = () => {
    if (requireBankSelection) {
      return (
        <div className="col-md-6 mb-3">
          <label htmlFor="selectedBank" className="form-label">
            Banco <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faBuildingColumns} />
            </span>
            {banksLoading ? (
              <select
                id="selectedBank"
                className="form-select"
                disabled
                aria-label="Cargando bancos"
              >
                <option value="">Cargando bancos…</option>
              </select>
            ) : banksError ? (
              <div className="form-control d-flex align-items-center justify-content-between">
                <span className="text-danger">{banksError}</span>
                <Button variant="outlinePrimary" type="button" onClick={retryBanks}>
                  Reintentar
                </Button>
              </div>
            ) : (
              <select
                id="selectedBank"
                className={`form-select ${!selectedBankValue ? 'is-invalid' : ''}`}
                value={selectedBankValue}
                onChange={handleBankSelectChange}
                aria-label="Seleccioná un banco"
              >
                <option value="">Seleccioná un banco</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          {invalidBankMessage && (
            <div className="form-text text-danger">
              El banco registrado ya no está disponible. Seleccioná otro banco.
            </div>
          )}
          {!banksLoading && !banksError && !selectedBankValue && (
            <div className="form-text text-danger">
              Debe seleccionar un banco antes de continuar
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="col-md-6 mb-3">
        <label htmlFor="selectedBank" className="form-label">
          Banco seleccionado <span className="text-danger">*</span>
        </label>
        <div className="input-group">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faBuildingColumns} />
          </span>
          <input
            type="text"
            className="form-control"
            id="selectedBank"
            value={bankData ? bankData.name : 'No se ha seleccionado un banco'}
            readOnly
            style={{ backgroundColor: '#f8f9fa' }}
          />
          {allowEditBankInfo && (
            <Button
              variant="outlinePrimary"
              type="button"
              onClick={handleBankChangeClick}
              title="Cambiar banco"
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          )}
        </div>
        {!bankData && (
          <div className="form-text text-danger">
            Debe seleccionar un banco antes de continuar
          </div>
        )}
        {bankData && allowEditBankInfo && (
          <div className="form-text text-muted">
            Puede cambiar el banco haciendo clic en el botón de editar
          </div>
        )}
        {bankData && !allowEditBankInfo && (
          <div className="form-text text-muted">
            Banco seleccionado confirmado
          </div>
        )}
      </div>
    );
  };

  const renderCbuField = () => (
    <div className="col-md-6 mb-3">
      <label htmlFor="cbu" className="form-label">
        {cbuLabel} <span className="text-danger">*</span>
      </label>
      <div className="input-group">
        <span className="input-group-text">
          <FontAwesomeIcon icon={faBuildingColumns} />
        </span>
        <input
          type="text"
          className={`form-control ${errors.cbu ? 'is-invalid' : touchedFields.cbu && !errors.cbu ? 'is-valid' : ''}`}
          id="cbu"
          name="cbu"
          value={formData.cbu || ''}
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          placeholder="Ingrese su CBU (22 dígitos)"
          maxLength="22"
        />
        {errors.cbu && (
          <div className="invalid-feedback">
            {errors.cbu}
          </div>
        )}
      </div>
    </div>
  );

  const renderSectionContent = () => (
      <div className="row">
        {renderBankField()}
        {renderCbuField()}
      </div>
  );

  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <h5 className="d-flex align-items-center mb-0 fw-light rg-sale-section__title">
            <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
            Información Bancaria
            {sectionValidity[sectionName] === true && (
              <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
            )}
            {sectionValidity[sectionName] === false && (
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
            )}
          </h5>
        </div>
        <div className="section-content">
          {renderSectionContent()}
        </div>

        {showBankModal && clientData && (
          <BankSelectionModal
            client={clientData}
            onConfirm={handleBankConfirm}
            onClose={handleBankModalClose}
            loading={false}
          />
        )}
      </div>
    );
  }

  return (
    <div className="accordion-item mb-1">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
          Información Bancaria{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        id={`collapse${sectionName}`}
      >
        <div className="accordion-body">
          {renderSectionContent()}
        </div>
      </div>

      {showBankModal && clientData && (
        <BankSelectionModal
          client={clientData}
          onConfirm={handleBankConfirm}
          onClose={handleBankModalClose}
          loading={false}
        />
      )}
    </div>
  );
});

export default BankInformation;
